import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ScreenLayout, Header, FormHeader } from '../components';
import { Input } from '../components/design-system';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { createTabPressHandler } from '../navigation/handleTabPress';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useThemedStyles } from '../hooks/useThemedStyles';

type Category = 'scanner' | 'printer' | 'camera' | 'other';
type ScannerType = 'pos' | 'a4';
type PrinterType = 'a4' | 'receipt';
type ConnectionType = 'usb' | 'network' | 'bluetooth' | 'wifi' | 'cloud';
type DeviceOnlineStatus = 'online' | 'offline' | 'idle' | 'error';

function OptionPicker<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string; icon?: string }[];
  value: T | undefined;
  onChange: (v: T) => void;
}) {
  const styles = useThemedStyles(getStyles);
  return (
    <View style={styles.pickerGroup}>
      <Text style={styles.fieldLabel}>{label}</Text>
      <View style={styles.optionRow}>
        {options.map((opt) => (
          <TouchableOpacity
            key={opt.value}
            style={[styles.optionChip, value === opt.value && styles.optionChipActive]}
            onPress={() => onChange(opt.value)}
            activeOpacity={0.7}
          >
            {opt.icon && (
              <Ionicons name={opt.icon as any} size={15} color={value === opt.value ? colors.white : colors.textLight} />
            )}
            <Text style={[styles.optionChipText, value === opt.value && styles.optionChipTextActive]}>
              {opt.label}
            </Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );
}

interface DeviceEditScreenProps {
  deviceId?: string;
}

export const DeviceEditScreen: React.FC<DeviceEditScreenProps> = ({ deviceId }) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo, activeTab, setActiveTab, showNotification } = useAppContext();

  const [loadingDevice, setLoadingDevice] = useState(true);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<Category | undefined>(undefined);
  const [scannerType, setScannerType] = useState<ScannerType | undefined>(undefined);
  const [printerType, setPrinterType] = useState<PrinterType | undefined>(undefined);
  const [connectionType, setConnectionType] = useState<ConnectionType | undefined>(undefined);
  const [ipAddress, setIpAddress] = useState('');
  const [port, setPort] = useState('');
  const [macAddress, setMacAddress] = useState('');
  const [serialNumber, setSerialNumber] = useState('');
  const [location, setLocation] = useState('');
  const [deviceStatus, setDeviceStatus] = useState<DeviceOnlineStatus>('offline');
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!deviceId) { setLoadingDevice(false); return; }
    apiService.getDeviceById(deviceId).then((res) => {
      if (res.statusCode === 1000 && res.data) {
        const d = res.data;
        setName(d.name || '');
        setDescription(d.description || '');
        setCategory(d.category);
        setScannerType(d.scannerType);
        setPrinterType(d.printerType);
        setConnectionType(d.connectionType);
        setIpAddress(d.ipAddress || '');
        setPort(d.port ? String(d.port) : '');
        setMacAddress(d.macAddress || '');
        setSerialNumber(d.serialNumber || '');
        setLocation(d.location || '');
        setDeviceStatus(d.deviceStatus || 'offline');
      } else {
        showNotification('error', 'Error', 'Device not found');
        navigateTo('devices');
      }
    }).catch(() => {
      showNotification('error', 'Error', 'Failed to load device');
      navigateTo('devices');
    }).finally(() => setLoadingDevice(false));
  }, [deviceId]);

  const validate = () => {
    const e: Record<string, string> = {};
    if (!name.trim()) e.name = 'Name is required';
    if (!category) e.category = 'Category is required';
    if (!connectionType) e.connectionType = 'Connection type is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !deviceId) return;
    try {
      setSaving(true);
      const res = await apiService.updateDevice(deviceId, {
        name: name.trim(),
        description: description.trim() || undefined,
        category,
        scannerType: category === 'scanner' ? scannerType : undefined,
        printerType: category === 'printer' ? printerType : undefined,
        connectionType,
        ipAddress: ipAddress.trim() || undefined,
        port: port ? parseInt(port) : undefined,
        macAddress: macAddress.trim() || undefined,
        serialNumber: serialNumber.trim() || undefined,
        deviceStatus,
        location: location.trim() || undefined,
      });
      if (res.statusCode === 1000) {
        showNotification('success', 'Saved', 'Device updated');
        navigateTo('devices');
      } else {
        showNotification('error', 'Error', res.errors?.[0]?.message || 'Failed to update');
      }
    } catch (e: any) {
      showNotification('error', 'Error', e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTabPress = createTabPressHandler(navigateTo, setActiveTab);

  const showNetworkFields = connectionType === 'network' || connectionType === 'wifi';

  if (loadingDevice) {
    return (
      <ScreenLayout
        header={<Header showNotifications={false} showMessages={false} showNavigation={Platform.OS === 'web'} activeTab={activeTab} onTabPress={handleTabPress} />}
        showBottomTabs={Platform.OS !== 'web'}
        onLeftPaneClose={() => {}}
        leftPaneContent="settings"
        onSettingsItemPress={async (itemId) => {
          const { setPendingSettingsItem } = await import('../utils/tempStorage');
          await setPendingSettingsItem(itemId);
          navigateTo('settings');
        }}
        onRightPaneClose={() => {}}
      >
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={colors.primary} />
        </View>
      </ScreenLayout>
    );
  }

  return (
    <ScreenLayout
      header={
        <Header
          showNotifications={true}
          showMessages={true}
          onNotificationPress={() => navigateTo('notifications')}
          onMessagePress={() => navigateTo('chat')}
          showNavigation={Platform.OS === 'web'}
          activeTab={activeTab}
          onTabPress={handleTabPress}
        />
      }
      showBottomTabs={Platform.OS !== 'web'}
      onLeftPaneClose={() => {}}
      leftPaneContent="settings"
      onSettingsItemPress={async (itemId) => {
        const { setPendingSettingsItem } = await import('../utils/tempStorage');
        await setPendingSettingsItem(itemId);
        navigateTo('settings');
      }}
      onRightPaneClose={() => {}}
      formHeader={
        <FormHeader
          title="Edit Device"
          subtitle={name || 'Device details'}
          onBack={() => navigateTo('devices')}
          actions={[{ icon: 'checkmark', primary: true, onPress: handleSave, loading: saving, disabled: saving }]}
        />
      }
    >
      <View style={styles.container}>
        {/* Form */}
        <ScrollView contentContainerStyle={styles.content} showsVerticalScrollIndicator={false}>
          {/* Basic info */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Basic Info</Text>
            <Input label="Device Name *" value={name} onChangeText={setName} placeholder="e.g. Front Desk Scanner" error={errors.name} />
            <Input label="Description" value={description} onChangeText={setDescription} placeholder="Optional notes" />
            <Input label="Location" value={location} onChangeText={setLocation} placeholder="e.g. Warehouse A" />
          </View>

          {/* Status */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Status</Text>
            <OptionPicker<DeviceOnlineStatus>
              label="Device Status"
              value={deviceStatus}
              onChange={setDeviceStatus}
              options={[
                { value: 'online', label: 'Online' },
                { value: 'idle', label: 'Idle' },
                { value: 'offline', label: 'Offline' },
                { value: 'error', label: 'Error' },
              ]}
            />
          </View>

          {/* Device type */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Device Type</Text>
            {errors.category && <Text style={styles.fieldError}>{errors.category}</Text>}
            <OptionPicker<Category>
              label="Category"
              value={category}
              onChange={(v) => { setCategory(v); setScannerType(undefined); setPrinterType(undefined); }}
              options={[
                { value: 'scanner', label: 'Scanner', icon: 'barcode-outline' },
                { value: 'printer', label: 'Printer', icon: 'print-outline' },
                { value: 'camera', label: 'Camera', icon: 'camera-outline' },
                { value: 'other', label: 'Other', icon: 'hardware-chip-outline' },
              ]}
            />
            {category === 'scanner' && (
              <OptionPicker<ScannerType>
                label="Scanner Type"
                value={scannerType}
                onChange={setScannerType}
                options={[
                  { value: 'pos', label: 'POS / Handheld' },
                  { value: 'a4', label: 'A4 Flatbed' },
                ]}
              />
            )}
            {category === 'printer' && (
              <OptionPicker<PrinterType>
                label="Printer Type"
                value={printerType}
                onChange={setPrinterType}
                options={[
                  { value: 'a4', label: 'A4 Printer' },
                  { value: 'receipt', label: 'Receipt / Thermal' },
                ]}
              />
            )}
          </View>

          {/* Connection */}
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Connection</Text>
            {errors.connectionType && <Text style={styles.fieldError}>{errors.connectionType}</Text>}
            <OptionPicker<ConnectionType>
              label="Connection Type"
              value={connectionType}
              onChange={setConnectionType}
              options={[
                { value: 'usb', label: 'USB' },
                { value: 'network', label: 'Network' },
                { value: 'wifi', label: 'Wi-Fi' },
                { value: 'bluetooth', label: 'Bluetooth' },
                { value: 'cloud', label: 'Cloud' },
              ]}
            />
            {showNetworkFields && (
              <View style={styles.row}>
                <View style={styles.flex3}>
                  <Input label="IP Address" value={ipAddress} onChangeText={setIpAddress} placeholder="192.168.1.100" keyboardType="numbers-and-punctuation" />
                </View>
                <View style={styles.flex1}>
                  <Input label="Port" value={port} onChangeText={setPort} placeholder="9100" keyboardType="number-pad" />
                </View>
              </View>
            )}
            <Input label="MAC Address" value={macAddress} onChangeText={setMacAddress} placeholder="AA:BB:CC:DD:EE:FF" autoCapitalize="characters" />
            <Input label="Serial Number" value={serialNumber} onChangeText={setSerialNumber} placeholder="Optional" />
          </View>
        </ScrollView>
      </View>
    </ScreenLayout>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: colors.lightGray,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  content: {
    padding: spacing.md,
    gap: spacing.md,
    paddingBottom: Platform.OS !== 'web' ? 100 : spacing.lg,
  },
  section: {
    backgroundColor: colors.background.surface,
    borderRadius: designTokens.borderRadius.lg,
    padding: spacing.md,
    gap: spacing.sm,
    ...designTokens.shadows.sm,
  },
  sectionTitle: {
    fontSize: typography.fontSize.sm,
    fontWeight: '600',
    color: colors.textLight,
    fontFamily: typography.fontFamily.semibold,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: spacing.xs,
  },
  fieldLabel: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
  },
  pickerGroup: { gap: spacing.xs },
  optionRow: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.xs },
  optionChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.xs,
    borderRadius: 20,
    borderWidth: 1.5,
    borderColor: colors.border,
    backgroundColor: colors.background.surface,
  },
  optionChipActive: {
    backgroundColor: colors.primary,
    borderColor: colors.primary,
  },
  optionChipText: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.medium,
  },
  optionChipTextActive: { color: colors.white },
  row: { flexDirection: 'row', gap: spacing.sm },
  flex3: { flex: 3 },
  flex1: { flex: 1 },
  fieldError: {
    fontSize: typography.fontSize.xs,
    color: colors.error,
    fontFamily: typography.fontFamily.regular,
  },
});
